import React from 'react'
import GlassPanel from '../UI/GlassPanel'
import FormField from '../Forms/FormField'

const AttendanceSection = ({ event, onChange, onAttendedDatesChange, onRemoveAttendedDate, onAddAttendedDate }) => {
  // Check if event is in the future
  const isFutureEvent = event.endDate && new Date(event.endDate) > new Date()

  const handleAttendanceTypeChange = (e) => {
    const value = e.target.value
    onChange({
      target: {
        name: 'attendanceType',
        value: value,
        type: 'select'
      }
    })

    // Auto-set attended and wasStreamed based on attendance type
    const updates = {
      attended: value === 'inPerson' || value === 'both',
      wasStreamed: value === 'streamed' || value === 'both'
    }

    Object.entries(updates).forEach(([key, val]) => {
      onChange({
        target: {
          name: key,
          value: val,
          type: 'checkbox'
        }
      })
    })
  }

  return (
    <GlassPanel className="p-6 mb-8">
      <h2 className="text-xl font-semibold text-white mb-6">Attendance Information</h2>

      {/* Future Event Warning */}
      {isFutureEvent && (
        <div className="mb-6 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
          <div className="flex items-center gap-2 text-blue-200">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">
              This is a future event. Attendance tracking will be available after the event ends on {new Date(event.endDate).toLocaleDateString()}.
            </span>
          </div>
        </div>
      )}

      {/* Attendance Type */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-200 mb-3">How did you experience this event?</label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { value: 'none', label: 'Didn\'t Attend', desc: 'Just tracking the event' },
            { value: 'inPerson', label: 'Attended In-Person', desc: 'Was physically there' },
            { value: 'streamed', label: 'Watched Stream', desc: 'Watched online/TV' },
            { value: 'both', label: 'Both', desc: 'Attended + watched stream' }
          ].map((option) => (
            <label
              key={option.value}
              className={`relative p-4 rounded-xl border-2 transition-all duration-300 ${
                isFutureEvent && option.value !== 'none'
                  ? 'opacity-50 cursor-not-allowed border-white/10 bg-white/5'
                  : event.attendanceType === option.value
                  ? 'border-purple-500 bg-purple-500/20 cursor-pointer'
                  : 'border-white/20 bg-white/5 hover:border-white/40 cursor-pointer'
              }`}
            >
              <input
                type="radio"
                name="attendanceType"
                value={option.value}
                checked={event.attendanceType === option.value}
                onChange={handleAttendanceTypeChange}
                disabled={isFutureEvent && option.value !== 'none'}
                className="absolute opacity-0"
              />
              <div className="text-center">
                <div className="text-lg mb-2">{option.label}</div>
                <div className="text-xs text-gray-300">{option.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Attendance Details */}
      {event.attendanceType !== 'none' && (
        <div className="mb-6">
          <FormField
            label="Attendance Details"
            name="attendanceDetails"
            value={event.attendanceDetails}
            onChange={onChange}
            placeholder="Any special details about your attendance..."
            multiline
            rows={2}
          />
        </div>
      )}

      {/* Attended Dates (for multi-day events) */}
      {(event.attended || event.attendanceType === 'inPerson' || event.attendanceType === 'both') && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-200 mb-3">
            Dates Attended (for multi-day events)
          </label>
          <div className="space-y-3">
            {event.attendedDates.map((dateItem, index) => (
              <div key={index} className="flex gap-3 items-end">
                <div className="flex-1">
                  <FormField
                    type="date"
                    label={`Date ${index + 1}`}
                    value={dateItem.date}
                    onChange={(e) => onAttendedDatesChange(index, 'date', e.target.value)}
                  />
                </div>
                <div className="flex-2">
                  <FormField
                    label="Description (optional)"
                    value={dateItem.description}
                    onChange={(e) => onAttendedDatesChange(index, 'description', e.target.value)}
                    placeholder="What happened this day?"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveAttendedDate(index)}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg font-medium h-fit"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={onAddAttendedDate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
            >
              Add Another Date
            </button>
          </div>
        </div>
      )}

      {/* Top 10 Toggle */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="top10"
          name="top10"
          checked={event.top10}
          onChange={onChange}
          className="w-5 h-5 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500"
        />
        <label htmlFor="top10" className="text-white font-medium">
          Add to Top 10 Events
        </label>
      </div>
    </GlassPanel>
  )
}

export default AttendanceSection